import React, { Fragment } from 'react';

import axios from 'axios';

import { getUser } from '../actions/account';
import { getEntity, getEntityGrids } from '../actions/entity';

import auth from '../utils/auth';
import cartUtils from '../utils/cart';

import { Link, useParams } from 'react-router-dom';
import FormattedNumber from '../components/FormattedNumber.jsx';
import Loading from '../components/Loading.jsx';
import AddressBlock from '../components/user/AddressBlock.jsx';
import ProductTable from '../components/ProductTable.jsx';
import Totals from '../components/checkout/Totals.jsx';
import CreditCardLogo from '../components/CreditCardLogo.jsx';

import Alert from 'react-s-alert';

const OrderView = () => {
    const params = useParams();
    const [inProgress, setInProgress] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [isSendingEmail, setIsSendingEmail] = React.useState(false);
    const [order, setOrder] = React.useState({});
    const [org, setOrg] = React.useState({});
    const [user, setUser] = React.useState({});
    const [grids, setGrids] = React.useState({
        templates: [],
        columns: []
    });

    React.useEffect(() => {
        (async () => {
            try {
                let { data: order } = await axios.request({
                    url: `/orders/${params.order_number}`,
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Session-Key': auth.getSID()
                    }
                });
                setOrder(order);

                let { data: user } = await getUser(order.customer_id);
                setUser(user);

                let { data: org } = await getEntity(order.netsuite_entity);
                setOrg(org);

                let { data: grids } = await getEntityGrids(order.netsuite_entity);
                setGrids(grids);

                setInProgress(false);
            } catch (error) {
                setInProgress(false);
                setError(error);
            }
        })();
    }, [params.order_number]);

    const handleResendEmail = async () => {
        try {
            setIsSendingEmail(true);
            await axios.request({
                url: `/customers/${user._id}/orders/${order.order_number}/send-email`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Session-Key': auth.getSID()
                }
            });
            setIsSendingEmail(false);
            Alert.success('Confirmation re-sent');
        } catch {
            setIsSendingEmail(false);
            Alert.error('There was an error sending the confirmation');
        }
    };

    const getHumanPaymentType = (type) => {
        switch (type) {
            case 'cc':
                return 'Credit Card';
            case 'invoice':
                return 'Invoice Me';
            case 'credit':
                return 'Credit';
            default:
                return 'Unknown';
        }
    };

    let billing_address = order.billing_address ? order.billing_address : order.shipping_address;
    let shipping_address = order.shipping_address ? order.shipping_address : order.billing_address;
    let isSplit = !!params.sub_order_number;
    if (inProgress || !user._id || !org.entity_id) {
        return <Loading />;
    }
    if (error) {
        return <div>Error Loading Order</div>;
    }
    return (
        <div className="container">
            <div className="row f-align-center between-md">
                <div className="col-xs-12 col-md">
                    <h1 className="collapse-md">Order #{order.order_number}</h1>
                </div>
                <div className="col-xs-12 col-md-4 end-md">
                    <button className="button full-xs auto-md" onClick={handleResendEmail} disabled={isSendingEmail}>
                        {isSendingEmail ? <Loading inline /> : 'Re-Send Confirmation Email'}
                    </button>
                </div>
            </div>
            <hr />
            <div className="row">
                <div className="col-xs-12 col-sm-6">
                    <ul className="order-attributes list-flat">
                        <li className="order-date">
                            <strong>Purchase Date:</strong> {new Date(order._created).toLocaleDateString('en-US')}
                        </li>
                        <li className="order-total">
                            <strong>Order Total:</strong>{' '}
                            <FormattedNumber value={order.grand_total || 0} currency={org.currency} />
                        </li>
                    </ul>

                    <ul className="list-flat">
                        <li className="order-payment">
                            <strong>Payment Method:</strong> {getHumanPaymentType(order?.payment?.type)}{' '}
                            {order?.payment?.card_type && <CreditCardLogo brand={order?.payment?.card_type} />}
                        </li>
                        <li className="order-payment">
                            <strong>PO Number:</strong> {order?.payment?.po_number || ''}
                        </li>
                        {order?.payment && order?.payment?.type === 'cc' && (
                            <Fragment>
                                <li>
                                    <strong>Expiration Date:</strong> {order.payment.expire_month}/
                                    {order.payment.expire_year}
                                </li>
                                <li>
                                    <strong>Card Last 4:</strong> {order.payment.last4}
                                </li>
                                <li>
                                    <strong>PayPal Transaction ID:</strong> {order.payment.transaction_id}
                                </li>
                            </Fragment>
                        )}
                        {order.is_return && (
                            <li>
                                <strong>RA Number:</strong> {order.ra_number_readable || 'Unavailable'}
                            </li>
                        )}
                    </ul>

                    {order?.order_status?.[0]?.shipment_tracking && (
                        <ul className="list-flat">
                            {order.order_status[0].shipment_tracking.shipping_method.includes('FedEx') &&
                                order?.order_status?.[0]?.shipment_tracking.shipping_tracking_numbers && (
                                    <li className="order-tracking-number word-break">
                                        <strong>Tracking Number: </strong>
                                        <a
                                            href={`https://www.fedex.com/apps/fedextrack/?action=track&action=track&tracknumbers=${order.order_status[0].shipment_tracking.shipping_tracking_numbers.join(
                                                ','
                                            )}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {order.order_status[0].shipment_tracking.shipping_tracking_numbers.join(
                                                ','
                                            )}
                                        </a>
                                    </li>
                                )}
                            {!order.order_status[0].shipment_tracking.shipping_method.includes('FedEx') &&
                                order?.order_status?.[0]?.shipment_tracking.shipping_tracking_numbers && (
                                    <li className="order-tracking-number word-break">
                                        <strong>Tracking Number: </strong>
                                        {order.order_status[0].shipment_tracking.shipping_tracking_numbers.join(',')}
                                    </li>
                                )}
                            {order.order_status[0].shipment_tracking.shipping_method && (
                                <li className="order-total">
                                    <strong>Shipping Method: </strong>
                                    {order.order_status[0].shipment_tracking.shipping_method}
                                </li>
                            )}
                        </ul>
                    )}

                    <ul className="list-flat">
                        {order.customer_name && (
                            <li>
                                <strong>Ordered By:</strong>{' '}
                                <Link to={`/customer/${order.customer_id}`}>
                                    {order.customer_name.given} {order.customer_name.family}
                                </Link>{' '}
                                ({order.email})
                            </li>
                        )}
                        {order.submitted_by && (
                            <li>
                                <strong>Submitted By:</strong> {order.submitted_by}
                            </li>
                        )}
                    </ul>
                </div>
                <div className="col-xs-12 col-sm-6">
                    <strong>Billing Address:</strong>
                    <div className="spaced-single">
                        {billing_address ? (
                            <AddressBlock name={billing_address.to} address={billing_address} />
                        ) : (
                            <div>No Billing Address</div>
                        )}
                    </div>

                    <strong>Shipping Address:</strong>
                    {shipping_address ? (
                        <AddressBlock name={shipping_address.to} address={shipping_address} />
                    ) : (
                        <div>No Shipping Address</div>
                    )}
                </div>
            </div>

            {!isSplit && order.order_status && !!order.order_status.length && (
                <div className="spaced-double">
                    <h3>Orders</h3>
                    <table className="table simple-table">
                        <thead>
                            <tr>
                                <th>Order Number</th>
                                <th>Est. Ship Date</th>
                                <th>Total</th>
                                <th>Status</th>
                                <th>Invoice</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(order.order_status || []).map((split, index) => (
                                <tr key={`order_${order.order_number}_${index}`}>
                                    <td>{order.order_number !== split.nsOrderNumber && split.nsOrderNumber}</td>
                                    <td>{new Date(split.ship_date).toLocaleDateString('en-US')}</td>
                                    <td>
                                        {split.order_total ? (
                                            <FormattedNumber value={split.order_total} currency={org.currency} />
                                        ) : (
                                            '---'
                                        )}
                                    </td>
                                    <td>{split.status}</td>
                                    <td>
                                        {(split.invoice_numbers || []).length ? (
                                            <ul className="list-flat list-collapse">
                                                {split.invoice_numbers.map((num) => (
                                                    <li>
                                                        <a
                                                            href={`${process.env.REACT_APP_API_URL}/orders/invoice/${num}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {num}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            '---'
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <h2 className="table-caption">Items Ordered</h2>

            <ProductTable
                account={{
                    info: user,
                    org: org,
                    grid: grids
                }}
                meta={['authors', 'narrators', 'isbn', 'subtitle', 'studio', 'brand', 'sku', 'orig_order_number']}
                currency={org.currency}
                products={order.items}
                ilsEnabled={user.ils_enabled}
                showAllocations={org.ils_enabled}
                showCheckbox={false}
                isReadOnly
                isCart
            />

            <div className="row reverse spaced-double">
                <div className="col-xs-12 col-md-5">
                    <Totals
                        sub_total={order.sub_total}
                        discount={order.discount}
                        shipping={order.shipping}
                        grand_total={order.grand_total}
                        currency={org.currency}
                        unit_count={cartUtils.getUnitCount(order.items)}
                    />
                </div>
            </div>

            <div className="txt-center spaced-double">
                <Link to={`/customer/${user._id}/orders`} className="button button-primary full-xs auto-sm">
                    Back to User Order History
                </Link>
            </div>
        </div>
    );
};

export default OrderView;
